import { useCallback, useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { rolesOf } from "../lib/roles";
import { Empty, Field, Loading, PageHeader, Panel } from "../components/common";
import { CertManager } from "../components/CertManager";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const LABELS = { nome: "Nome", cognome: "Cognome", telefono: "Telefono", data_nascita: "Data di nascita", luogo_nascita: "Luogo di nascita", codice_fiscale: "Codice fiscale",
  indirizzo: "Indirizzo", citta: "Città", data_visita_medica: "Data visita medica", visita_medica_validita: "Validità visita (anni)", ore_settimanali: "Ore settimanali", giorni_settimanali: "Giorni settimanali" };

function Course({ uid, onDone }) {
  const [f, setF] = useState({ nome: "", data: "", validita_anni: "", ente: "" });
  const save = async () => {
    if (!f.nome || !f.data) return toast.error("Indica corso e data");
    const [y, m, d] = f.data.split("-");
    try { await api("/admin/courses/completed", { method: "POST", body: { user_id: uid, nome: f.nome, data: `${d}/${m}/${y}`, validita_anni: Number(f.validita_anni || 0), ente: f.ente } }); toast.success("Corso registrato"); setF({ nome: "", data: "", validita_anni: "", ente: "" }); onDone?.(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="grid sm:grid-cols-4 gap-2 items-end" data-testid="vig-course-form">
      <Field label="Corso effettuato"><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="vig-course-name" /></Field>
      <Field label="Data"><Input type="date" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="vig-course-date" /></Field>
      <Field label="Validità (anni)"><Input type="number" min="0" value={f.validita_anni} onChange={(e) => setF({ ...f, validita_anni: e.target.value })} data-testid="vig-course-validity" /></Field>
      <Button onClick={save} className="rounded-full bg-[var(--teal)]" data-testid="vig-course-save">Aggiungi corso</Button>
    </div>
  );
}

export default function VigStaff() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api("/vig/staff").then(setD).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api(`/vig/staff/${edit.id}`, { method: "PUT", body: Object.fromEntries(d.fields.map((k) => [k, k === "visita_medica_validita" ? (edit[k] === "" || edit[k] == null ? null : Number(edit[k])) : edit[k] || ""])) }); toast.success("Anagrafica aggiornata"); load(); } catch (e) { toast.error(e.message); }
  };
  if (d === null) return <Loading />;
  if (d === false) return <Empty title="Non disponibile" text="Questa sezione funziona dopo il passaggio alla web app indipendente." />;
  return (
    <div>
      <PageHeader subtitle="Responsabile vigilanza" title="Personale Vigilanza" />
      <Panel className="!p-0" data-testid="vig-staff-list">{d.users.map((u) => (
        <div key={u.id} className="flex items-center justify-between px-5 py-3 border-b last:border-0">
          <div><p className="font-semibold text-[var(--navy)]">{u.cognome} {u.nome}</p><p className="text-xs text-slate-500">{rolesOf(u).join(" · ")}</p></div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit({ ...u })} data-testid={`vig-staff-edit-${u.id}`}><Pencil className="w-4 h-4 mr-1" />Scheda</Button>
        </div>))}
      </Panel>
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.cognome} {edit?.nome}</DialogTitle></DialogHeader>
          {edit && <>
            <div className="grid sm:grid-cols-2 gap-3">{d.fields.map((k) => (
              <Field key={k} label={LABELS[k] || k}><Input value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`vig-field-${k}`} /></Field>))}
            </div>
            <Button onClick={save} className="rounded-full bg-[var(--navy)] w-fit" data-testid="vig-staff-save">Salva anagrafica</Button>
            <h3 className="font-display font-bold text-[var(--navy)] mt-4">Corsi</h3>
            <Course uid={edit.id} />
            <h3 className="font-display font-bold text-[var(--navy)] mt-4">Brevetti e certificazioni</h3>
            <CertManager basePath={`/admin/users/${edit.id}/certifications`} />
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
